import { useState } from "react";
import { Input } from "reactstrap";

import { ISchedule } from "Pages/Setup/Scheduler/Scheduler";
import styles from "Pages/Setup/Scheduler/Components/AddSchedule/CronSelector/RateSelector/RateSelector.module.css";

interface IProps {
    schedule: ISchedule;
    onSetSchedule: (schedule: ISchedule) => void;
    isRateInvalid: boolean;
    onSetIsRateInvalid: (isRateInvalid: boolean) => void;
}

export default function RateSelector({
    schedule,
    onSetSchedule,
    isRateInvalid,
    onSetIsRateInvalid,
}: IProps) {
    const [rate, setRate] = useState(getDefaultRate());

    function getDefaultRate(): {
        unit: string;
        value: string;
        startDate: string; // This is an ISO UTC date string always on AWS: 2024-03-14T15:39:00.000Z
    } {
        const now = getDateStringForInput(new Date().toISOString());

        const defaultRate = {
            unit: "days",
            value: "1",
            startDate: now,
        };

        const matches = schedule.trigger.match(/rate\(([^)]+?)\)/i);
        if (matches) {
            const rateString = matches[1].split(" ");
            return {
                unit: rateString[1],
                value: rateString[0],
                startDate: getDateStringForInput(schedule.start_date || now),
            };
        }
        return defaultRate;
    }

    /**  Converts an ISO UTC date string to a local value fitting this format yyyy-mm-ddThh:mm
     * so it can be used as datetime-local input value
     */
    function getDateStringForInput(ISODateString: string) {
        const date = new Date(ISODateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const hour = String(date.getHours()).padStart(2, "0");
        const minute = String(date.getMinutes()).padStart(2, "0");
        const defaultStartDate = `${year}-${month}-${day}T${hour}:${minute}`;

        return defaultStartDate;
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        const newRate = { ...rate, [name]: value };
        setRate(newRate);

        const rateExpression = `rate(${newRate.value} ${newRate.unit})`;
        let newStartDate: string = schedule.start_date;
        try {
            newStartDate = new Date(newRate.startDate).toISOString();
        } catch {}

        onSetSchedule({
            ...schedule,
            trigger: rateExpression,
            start_date: newStartDate,
        });

        validateRate(newRate.startDate);
    };

    // Makes sure start date is not 5 minutes or later ago
    function validateRate(startDate: string) {
        const fiveMinAgo = new Date().getTime() - 5 * 60 * 1000;
        const startDateDate = new Date(startDate).getTime();

        if (startDateDate <= fiveMinAgo) {
            onSetIsRateInvalid(true);
        } else {
            onSetIsRateInvalid(false);
        }
    }

    const fiveMinAgo = new Date().getTime() - 5 * 60 * 1000;
    const fiveMinAgoString = getDateStringForInput(
        new Date(fiveMinAgo).toISOString()
    );

    return (
        <div>
            <div className={styles.valueInputBox}>
                <span>Run every</span>
                <Input
                    className={styles.valueInput}
                    type="select"
                    name="value"
                    value={rate.value}
                    onChange={handleChange}
                >
                    {/* make options from 1 - 30 */}
                    {Array.from({ length: 30 }, (_, i) => i + 1).map((i) => (
                        <option key={i} value={i}>
                            {i}
                        </option>
                    ))}
                </Input>
                <span>days, starting at</span>
                <Input
                    className={styles.datetimeInput}
                    type="datetime-local"
                    name="startDate"
                    value={rate.startDate} // Format like 2024-03-14T15:39
                    onChange={handleChange}
                    min={fiveMinAgoString}
                />

                {isRateInvalid && (
                    <div className={styles.error}>
                        Start date not be 5 minutes or more in the past
                    </div>
                )}
            </div>
        </div>
    );
}
